import React from 'react';
import { Wallet, Shield, Clock, Users, Building2, ChevronRight } from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';
import { useLanguage } from '../../contexts/LanguageContext';

function PaymentPage() {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-20 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Оплата поставщикам' : 
                 language === 'en' ? 'Payments to Suppliers' : 
                 '供应商付款'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                {language === 'ru' ? 'в Китае' : 
                 language === 'en' ? 'in China' : 
                 '在中国'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              {language === 'ru' 
                ? 'С 2013 года помогаем бизнесу эффективно и безопасно проводить оплату китайским поставщикам'
                : language === 'en' 
                ? 'Since 2013, we have been helping businesses efficiently and safely make payments to Chinese suppliers'
                : '自2013年以来，我们一直帮助企业高效安全地向中国供应商付款'}
            </p>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              {language === 'ru' ? 'Для малого и среднего бизнеса' : 
               language === 'en' ? 'For Small and Medium Businesses' : 
               '对于中小型企业'}
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              {language === 'ru' 
                ? 'Предлагаем удобные и безопасные способы оплаты, адаптированные под потребности вашего бизнеса.'
                : language === 'en'
                ? 'We offer convenient and secure payment methods adapted to the needs of your business.'
                : '我们提供适合您业务需求的便捷安全的支付方式。'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                <Wallet className="w-12 h-12 text-[#1B3BA0] mb-6" />
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">
                  {language === 'ru' ? 'Удобные способы оплаты' : 
                   language === 'en' ? 'Convenient Payment Methods' : 
                   '便捷的支付方式'}
                </h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-center">
                    <ChevronRight className="w-5 h-5 text-[#1B3BA0] mr-2" />
                    {language === 'ru' ? 'Перевод на российскую банковскую карту' : 
                     language === 'en' ? 'Transfer to a Russian bank card' : 
                     '转账到俄罗斯银行卡'}
                  </li>
                  <li className="flex items-center">
                    <ChevronRight className="w-5 h-5 text-[#1B3BA0] mr-2" />
                    {language === 'ru' ? 'Оплата в USDT' : 
                     language === 'en' ? 'Payment in USDT' : 
                     'USDT支付'}
                  </li>
                </ul>
              </div>
              <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                <Shield className="w-12 h-12 text-[#1B3BA0] mb-6" />
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">
                  {language === 'ru' ? 'Безопасный перевод' : 
                   language === 'en' ? 'Secure Transfer' : 
                   '安全转账'}
                </h3>
                <p className="text-gray-600">
                  {language === 'ru' 
                    ? 'Мы самостоятельно осуществим перевод в Китай и оплату поставщикам от вашего имени. Это быстро, удобно и безопасно. Все процессы отлажены годами практики.'
                    : language === 'en'
                    ? 'We will independently make the transfer to China and pay suppliers on your behalf. It\'s fast, convenient, and secure. All processes have been refined through years of practice.'
                    : '我们将独立地代表您向中国转账并向供应商付款。这既快速、方便又安全。所有流程都经过多年实践的完善。'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Для крупных компаний' : 
             language === 'en' ? 'For Large Companies' : 
             '对于大型公司'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <Building2 className="w-12 h-12 text-[#1B3BA0] mb-6" />
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">
                {language === 'ru' ? 'Брокерские услуги' : 
                 language === 'en' ? 'Brokerage Services' : 
                 '经纪服务'}
              </h3>
              <p className="text-gray-600 mb-4">
                {language === 'ru' 
                  ? 'Возможность перевода средств поставщику через нашего проверенного брокера.'
                  : language === 'en'
                  ? 'Possibility of transferring funds to the supplier through our trusted broker.'
                  : '通过我们值得信赖的经纪人向供应商转账的可能性。'}
              </p>
              <p className="text-[#1B3BA0] font-bold">
                {language === 'ru' 
                  ? 'Комиссия: от 1% до 2% в зависимости от суммы'
                  : language === 'en'
                  ? 'Commission: from 1% to 2% depending on the amount'
                  : '佣金：根据金额从1%到2%不等'}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <Users className="w-12 h-12 text-[#1B3BA0] mb-6" />
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">
                {language === 'ru' ? 'Консультации' : 
                 language === 'en' ? 'Consultations' : 
                 '咨询'}
              </h3>
              <p className="text-gray-600">
                {language === 'ru' 
                  ? 'Консультации по прямым международным переводам и помощь в подборе подходящего способа оплаты для вашего бизнеса.'
                  : language === 'en'
                  ? 'Consultations on direct international transfers and assistance in selecting the appropriate payment method for your business.'
                  : '关于直接国际转账的咨询，以及帮助为您的业务选择合适的支付方式。'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Получить консультацию' : 
             language === 'en' ? 'Get a Consultation' : 
             '获取咨询'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Оставьте заявку, и мы поможем выбрать оптимальный способ оплаты для вашего бизнеса'
              : language === 'en' 
              ? 'Leave a request, and we will help you choose the optimal payment method for your business'
              : '留下请求，我们将帮助您为您的业务选择最佳支付方式'}
          </p>
          <RequestForm source={language === 'ru' ? "Оплата поставщикам" : language === 'en' ? "Payments to Suppliers" : "供应商付款"} />
        </div>
      </section>
    </div>
  );
}

export default PaymentPage;